import { CheckoutWidgetsBuilderState } from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import {
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import { IntegrationKind } from "@redotech/redo-integrations-core";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo } from "react";
import { CheckoutWidgetsBuilderContext } from "./checkout-widgets-builder-context";
import * as css from "./preview-panel.module.css";
import { useFrameworkIntegrationStatus } from "./use-framework-integration-status";
import { useOkendoConnectionStatus } from "./use-okendo-connection-status";

type ProductReviewsWidget = NonNullable<
  CheckoutWidgetsBuilderState["checkoutWidgets"][CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]
>;

const SAMPLE_REVIEW = {
  rating: 5,
  title: "Exceeded my expectations",
  body: "Honestly the best purchase I've made all year. Shipping was fast and the quality is spot-on.",
  reviewerName: "Jamie L.",
  productName: "Example Product",
  verified: true,
} as const;

/**
 * Preview for the Product Reviews widget. Before the merchant connects a
 * reviews provider we show a short instructional placeholder; once connected,
 * we render a static sample review so the merchant can see how their display
 * toggles affect the layout. The live-in-checkout carousel lives in the
 * Shopify extension component.
 */
export const ProductReviewsWidgetPreview = memo(
  function ProductReviewsWidgetPreview() {
    const { checkoutWidgets, frameworkIntegrationApi, okendoIntegrationApi } =
      useContext(CheckoutWidgetsBuilderContext);
    const widget = useMemo(
      () => checkoutWidgets[CheckoutOptimizationWidgetType.PRODUCT_REVIEWS],
      [checkoutWidgets],
    );
    const { status: judgeMeStatus } = useFrameworkIntegrationStatus(
      frameworkIntegrationApi,
      IntegrationKind.Judgeme,
    );
    const { status: yotpoStatus } = useFrameworkIntegrationStatus(
      frameworkIntegrationApi,
      IntegrationKind.Yotpo,
    );
    const { status: stampedStatus } = useFrameworkIntegrationStatus(
      frameworkIntegrationApi,
      IntegrationKind.Stamped,
    );
    const { status: okendoStatus } =
      useOkendoConnectionStatus(okendoIntegrationApi);

    if (!widget) {
      return null;
    }

    const connected = (() => {
      switch (widget.provider) {
        case CheckoutReviewsProvider.JUDGE_ME: {
          return judgeMeStatus.state === "connected";
        }
        case CheckoutReviewsProvider.OKENDO: {
          return okendoStatus.state === "connected";
        }
        case CheckoutReviewsProvider.YOTPO: {
          return yotpoStatus.state === "connected";
        }
        case CheckoutReviewsProvider.STAMPED: {
          return stampedStatus.state === "connected";
        }
        case CheckoutReviewsProvider.REDO: {
          // First-party: no OAuth connection, so the sample always renders.
          return true;
        }
        default: {
          return assertNever(widget.provider);
        }
      }
    })();

    if (!connected) {
      return (
        <Flex
          align="center"
          bgColor="secondary"
          borderRadius="md"
          className={css.productReviewsWidgetPreview}
          dir="column"
          gap="sm"
          justify="center"
          p="3xl"
        >
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            Connect a review provider to see a preview
          </Text>
          <Text fontSize="sm" textColor="secondary">
            Reviews will be pulled from your provider for products in the
            buyer&apos;s cart.
          </Text>
        </Flex>
      );
    }

    return <SampleReview display={widget.display} />;
  },
);

const SampleReview = memo(function SampleReview({
  display,
}: {
  display: ProductReviewsWidget["display"];
}) {
  return (
    <Flex
      bgColor="secondary"
      borderRadius="md"
      className={css.productReviewsWidgetPreview}
      dir="column"
      gap="sm"
      p="xl"
    >
      <Flex align="center" dir="row" gap="sm">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {renderStars(SAMPLE_REVIEW.rating)}
        </Text>
        {display.showReviewerName ? (
          <Text fontSize="sm" textColor="secondary">
            {SAMPLE_REVIEW.reviewerName}
          </Text>
        ) : null}
        {display.showVerifiedBadge && SAMPLE_REVIEW.verified ? (
          <Text fontSize="sm" textColor="success">
            Verified
          </Text>
        ) : null}
      </Flex>
      {display.showProductName ? (
        <Text fontSize="sm" textColor="secondary">
          {SAMPLE_REVIEW.productName}
        </Text>
      ) : null}
      {display.showReviewTitle ? (
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {SAMPLE_REVIEW.title}
        </Text>
      ) : null}
      {display.showReviewBody ? (
        <Text fontSize="sm" textColor="primary">
          {SAMPLE_REVIEW.body}
        </Text>
      ) : null}
    </Flex>
  );
});

function renderStars(rating: number): string {
  const clamped = Math.max(0, Math.min(5, Math.round(rating)));
  return "★".repeat(clamped) + "☆".repeat(5 - clamped);
}
